import { NeutralButton } from '@systemeio/ui'
import RuleSave from 'modules/automation-rules/components/rule-save'
import { useAutomationRule } from 'modules/automation-rules/hooks/use-automation-rule'
import { useRuleState } from 'modules/automation-rules/hooks/use-rule-state'
import { SpecificTriggerRulesProps } from 'modules/automation-rules/specific-trigger-rules'
import { AutomationRuleTriggersEnum } from 'modules/automation-rules/triggers/enums/trigger-enum'
import {
  AutomationRuleApiInterface,
  AutomationRuleViewInterface,
} from 'modules/automation-rules/types/automation-rule-interface'
import { useEffect, useRef, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { KeyedMutator } from 'swr'
import { SpecificTriggerRule } from './specific-trigger-rule'
import { ArrowIonicLeftIcon } from '@systemeio/ui/icons'

interface SpecificTriggerRuleEditProps
  extends Pick<
    SpecificTriggerRulesProps,
    'getRelatedAndUnrelatedTriggersData' | 'customAddTriggerHandler' | 'noRelatedMessage'
  > {
  ruleId: number
  clearSelectedRule: () => void
  availableTriggers?: AutomationRuleTriggersEnum[]
  mutateRules: KeyedMutator<AutomationRuleApiInterface>
  mutateAvailableTriggers: KeyedMutator<AutomationRuleTriggersEnum[]>
}

const SpecificTriggerRuleEdit = ({
  ruleId,
  clearSelectedRule,
  mutateRules,
  mutateAvailableTriggers,
  availableTriggers,
  getRelatedAndUnrelatedTriggersData,
  customAddTriggerHandler,
  noRelatedMessage,
}: SpecificTriggerRuleEditProps) => {
  const { t } = useLocoTranslation()

  const { data: rule, mutate } = useAutomationRule(ruleId)

  const [isFetching, setIsFetching] = useState(false)

  const { tempState, setTempState, errors, setErrors } = useRuleState()

  const maxTriggerId = useRef(0)
  const maxActionId = useRef(0)

  useEffect(() => {
    if (rule) {
      setTempState(prev => ({ ...prev, ...rule }))
      if (rule.actions.length !== 0) {
        maxActionId.current = Math.max(...rule.actions.map(el => el.id))
      }
    }
  }, [rule, setTempState])

  const afterSave = async (rule: AutomationRuleViewInterface) => {
    await mutate(data => ({ ...data, ...rule }))
    await mutateRules()
    await mutateAvailableTriggers()
    clearSelectedRule()
  }

  return (
    <div className={'flex flex-col gap-5 px-6 py-4 lg:px-8'}>
      <div className={'grid grid-flow-col-dense grid-cols-2'}>
        <NeutralButton disabled={isFetching} className={'w-fit'} onClick={clearSelectedRule}>
          <ArrowIonicLeftIcon
            className={'group-hover:fill-brand-primary main-transition-colors lg:hidden'}
          />
          <span className={'hidden lg:inline'}>{t('dashboard.funnel.automation_rules.back')}</span>
        </NeutralButton>
        <div className={'flex justify-end'}>
          <RuleSave
            afterSave={afterSave}
            isFetching={isFetching}
            setIsFetching={setIsFetching}
            rule={tempState}
            ruleId={ruleId}
            setErrors={setErrors}
          />
        </div>
      </div>
      <SpecificTriggerRule
        ruleId={ruleId}
        isFetching={isFetching}
        availableTriggers={availableTriggers}
        tempState={tempState}
        setTempState={setTempState}
        errors={errors}
        maxTriggerId={maxTriggerId}
        maxActionId={maxActionId}
        isPreFetching={!rule}
        savedTriggers={rule?.triggers}
        getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData}
        customAddTriggerHandler={customAddTriggerHandler}
        noRelatedMessage={noRelatedMessage}
      />
    </div>
  )
}

export default SpecificTriggerRuleEdit
